// Filter und Sortierung der Seite Verkäufe als reine Funktionen, ohne Vue und DevExtreme (Kundenwunsch 12.10.2026:
// die Filter- und Sortiermöglichkeiten der früheren Filterzeile im neuen Design, ohne Backend-Änderung). Alles läuft im
// Browser über die geladenen Zeilen; der Server schränkt nur den Zeitraum ein. Nichts davon wird gespeichert.
//
// Filterzustand (emptyFilters): payment 'bar' | 'karte' | 'gutschein' (Ableitung in zahlart.js), storno 'ohne' | 'nur', direction 'hin' | 'rueck' (jeweils
// null = alle), je Liste (FILTER_LISTS) ein Array gewählter Schlüssel und priceMin/priceMax als eingegebener Text
// („2,50“). Schlüssel = String(Rohwert), '' = ohne Angabe (null, undefined, leer). Geprüft werden immer die Rohfelder
// der Zeile: DevExtreme vergleicht bei lokalen Daten sonst den berechneten Spaltenwert (Storno „X“, Zahlart
// „Kartenzahlung“) und filtert Spalten ohne Sichtbarkeit gar nicht.
import { formatEuro } from '@/utils/format';
import { zahlart } from './zahlart';

// Segmenttexte groß wie die übrigen Segmente („Aktiv | Inaktiv | Alle“, „Monat | Tag“); desc = Text in der Aufzählung
// der gesetzten Filter (Tooltip, Leerzustand, Druck: „Zahlart: Karte · ohne Stornos“)
export const PAYMENT_ITEMS = [
  { value: null, text: 'Alle' },
  { value: 'bar', text: 'Bar' },
  { value: 'karte', text: 'Karte' },
  { value: 'gutschein', text: 'Gutschein' }
];
// Original und Storno-Buchung tragen beide is_cancellation 1 (wie das Storno-Tag in der Liste)
export const STORNO_ITEMS = [
  { value: null, text: 'Alle' },
  { value: 'ohne', text: 'Ohne Stornos', desc: 'ohne Stornos' },
  { value: 'nur', text: 'Nur Stornos', desc: 'nur Stornos' }
];
// wie das Verkaufsdetail: 1 = Hinfahrt, jeder andere vorhandene Wert = Rückfahrt, ohne Wert keins von beiden
export const DIRECTION_ITEMS = [
  { value: null, text: 'Alle' },
  { value: 'hin', text: 'Hinfahrt' },
  { value: 'rueck', text: 'Rückfahrt' }
];

// Tarifzonen eines Verkaufs wie die Spalte „Tarif-Kombination“: Zonen von Start und Ziel, sortiert, mit Bindestrich
// (Filterliste „Tarifzonen“ und Gruppierung)
export function tariffZones(row) {
  return [row.location_from_tariff_zone_1, row.location_to_tariff_zone_1]
    .filter((zone) => zone != null)
    .sort()
    .join('-');
}

// Mehrfachlisten im Filter-Panel in dieser Reihenfolge; field = Rohfeld der Zeile, value(row) = berechneter Wert statt
// des Rohfelds. Fahrt und Tarifzonen seit der Prüfung vom 12.10.2026: Auch sie hatten in der früheren Filterzeile einen
// eigenen Filter. Tarifzonen-ID seit 16.10.2026 (Kundenrückmeldung: „wie kann ich im neuen nach Tarifzonen ID
// filtern?“, Exporte je Tarifzone für die Abrechnungen); die Liste der Zonen von Start und Ziel heißt seitdem wie die
// Excel-Spalte „Tarif-Kombination“ statt „Tarifzonen“, die sie mit der Tarifzonen-ID verwechseln ließ.
export const FILTER_LISTS = [
  { key: 'drivers', field: 'username', label: 'Fahrer' },
  { key: 'lines', field: 'line_id', label: 'Linie' },
  { key: 'trips', field: 'trip_id', label: 'Fahrt' },
  { key: 'from', field: 'location_from_name', label: 'Von' },
  { key: 'to', field: 'location_to_name', label: 'Nach' },
  { key: 'tickets', field: 'ticket_name', label: 'Ticket' },
  { key: 'tariffs', field: 'tariff_name', label: 'Tarif' },
  { key: 'tariffZoneIds', field: 'tariff_zone_id', label: 'Tarifzonen-ID' },
  { key: 'tariffZones', field: 'tariff_zone_combination', label: 'Tarif-Kombination', value: tariffZones },
  { key: 'priceLevels', field: 'price_level', label: 'Preisstufe' },
  { key: 'persons', field: 'ticket_count', label: 'Personen' },
  { key: 'devices', field: 'printer_id', label: 'Gerät' },
  { key: 'tenants', field: 'logo_suffix', label: 'Mandant' }
];

// Konfiguration der Filter (letzter, optionaler Parameter der Funktionen unten): Listen, Feld und Bezeichnung des
// Betrags; Standard = Verkäufe. Zahlungen nutzt dieselben Funktionen mit eigenen Listen (Kundenwunsch 13.10.2026,
// src/components/payments/paymentFilters.js).
export const SALE_FILTER_CONFIG = { lists: FILTER_LISTS, amountField: 'price', amountLabel: 'Preis' };

// Seiten mit eigenen Umschaltern (Stammdaten, Kundenwunsch 13.10.2026) geben config.segments mit:
// [{ key, label, items: [{ value, text }], defaultValue, match(value, row) }]; value null = alle. Ohne Angabe nur die
// Umschalter der Verkäufe (Zahlart, Storno, Richtung).
function configOf(config) {
  return config || SALE_FILTER_CONFIG;
}

function segmentsOf(config) {
  return configOf(config).segments || [];
}

// Zeitpunkte (Kundenwunsch 14.10.2026, Edzards: „Letzte Änderung 13.10.2026 < 05:24 Uhr … Da konnten wir immer nach
// Größer kleiner = Filtern“): config.times = [{ key, label, value(row) → Zeitstempel in ms oder null }]. Im Zustand je
// Zeitpunkt `${key}From` und `${key}To` als Text „13.10.2026 05:24“ bzw. „13.10.2026“ (parseDateTime). Jede Seite
// allein geht: nur von = ab (größer), nur bis = bis (kleiner), beide = dazwischen, gleiches Datum = dieser Tag.
export function timesOf(config) {
  return configOf(config).times || [];
}

export function timeFields(time) {
  return { from: time.key + 'From', to: time.key + 'To' };
}

// Grenzen eines Zeitpunkts in ms ({ min, max }, null = offen; beide einschließlich); ohne gültige Angabe null
export function timeBounds(fromText, toText) {
  const from = parseDateTime(fromText);
  const to = parseDateTime(toText, true);
  return from || to ? { min: from ? from.ms : null, max: to ? to.ms : null } : null;
}

// Schlüssel des Werts einer Liste in einer Zeile
function listKey(list, row) {
  return valueKey(list.value ? list.value(row) : row[list.field]);
}

export const NO_VALUE_TEXT = 'ohne Angabe';

// Deutsch mit Zahlen in natürlicher Folge („12“ vor „100“, „N2“ vor „N10“), ohne Groß-/Kleinschreibung
const collator = new Intl.Collator('de', { numeric: true, sensitivity: 'base' });

export function emptyFilters(config) {
  const filters = { payment: null, storno: null, direction: null, priceMin: '', priceMax: '' };
  segmentsOf(config).forEach((segment) => {
    filters[segment.key] = segment.defaultValue === undefined ? null : segment.defaultValue;
  });
  timesOf(config).forEach((time) => {
    const fields = timeFields(time);
    filters[fields.from] = '';
    filters[fields.to] = '';
  });
  configOf(config).lists.forEach((list) => {
    filters[list.key] = [];
  });
  return filters;
}

// vollständiger Zustand auch aus Teilobjekten (fehlende Listen = leer)
function normalize(filters, config) {
  const f = Object.assign(emptyFilters(config), filters || {});
  configOf(config).lists.forEach((list) => {
    f[list.key] = Array.isArray(f[list.key]) ? f[list.key].map(String) : [];
  });
  return f;
}

export function valueKey(value) {
  return value === null || value === undefined ? '' : String(value);
}

function defaultLabel(listKey, key) {
  return key === '' ? NO_VALUE_TEXT : key;
}

// „2,50“, „2.5“, „2,“, „−3,20“ → Zahl; leer oder unlesbar → null
export function parsePrice(text) {
  if (text === null || text === undefined) {
    return null;
  }
  let s = String(text).replace(/\s|€/g, '').replace(String.fromCharCode(0x2212), '-');
  if (s === '') {
    return null;
  }
  if (s.indexOf(',') !== -1) {
    s = s.replace(/\./g, '').replace(',', '.');
  }
  if (!/^-?(\d+(\.\d*)?|\.\d+)$/.test(s)) {
    return null;
  }
  const n = Number(s);
  return isFinite(n) ? n : null;
}

export function priceInvalid(text) {
  return String(text === null || text === undefined ? '' : text).trim() !== '' && parsePrice(text) === null;
}

// Datum mit optionaler Uhrzeit: „13.10.2026 05:24“, „13.10.2026, 5:24 Uhr“, „13.10.26 05.24“, „13.10.2026“; ohne Jahr
// („13.10. 05:24“) das laufende Jahr
const DATE_TIME = /^(\d{1,2})\.(\d{1,2})\.?(\d{4}|\d{2})?(?:\s*,?\s*(\d{1,2})[:.](\d{2})(?:[:.](\d{2}))?)?\s*(?:uhr)?$/i;

function pad2(n) {
  return (n < 10 ? '0' : '') + n;
}

// Eingabe → { ms, text } (text für die Beschreibung: „13.10.2026, 05:24“); leer oder unlesbar → null. von (end false)
// gilt ab Beginn der Minute, ohne Uhrzeit ab 00:00; bis (end true) einschließlich dieser Minute, ohne Uhrzeit bis zum
// Ende des Tages.
export function parseDateTime(text, end) {
  const s = String(text === null || text === undefined ? '' : text).trim();
  const m = s ? DATE_TIME.exec(s) : null;
  if (!m) {
    return null;
  }
  const day = Number(m[1]);
  const month = Number(m[2]) - 1;
  let year = m[3] ? Number(m[3]) : new Date().getFullYear();
  if (m[3] && m[3].length === 2) {
    year += 2000;
  }
  const hasTime = m[4] !== undefined;
  const hasSeconds = m[6] !== undefined;
  const hour = hasTime ? Number(m[4]) : 0;
  const minute = hasTime ? Number(m[5]) : 0;
  const second = hasSeconds ? Number(m[6]) : 0;
  if (hour > 23 || minute > 59 || second > 59) {
    return null;
  }
  const date = new Date(year, month, day, hour, minute, second, 0);
  if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) {
    return null;
  }
  let ms = date.getTime();
  if (end) {
    ms = hasTime ? ms + (hasSeconds ? 999 : 59999) : new Date(year, month, day + 1).getTime() - 1;
  }
  let shown = pad2(day) + '.' + pad2(month + 1) + '.' + year;
  if (hasTime) {
    shown += ', ' + pad2(hour) + ':' + pad2(minute) + (hasSeconds ? ':' + pad2(second) : '');
  }
  return { ms, text: shown };
}

export function dateTimeInvalid(text) {
  return String(text === null || text === undefined ? '' : text).trim() !== '' && parseDateTime(text) === null;
}

// Zeitauswahl im Panel (FilterTimeRange.vue): Datum und Uhrzeit getrennt, beides auch kurz getippt. Datum
// „13.10.2026“, „13.10.26“, „13.10.“, „13.10“ (laufendes Jahr) → 'DD.MM.YYYY'; Uhrzeit „5:24“, „05.24“, „524“, „0524“,
// „17“, „17 Uhr“ → 'HH:mm'. Leer → '', unlesbar → null.
const DATE_ONLY = /^(\d{1,2})\.(\d{1,2})\.?(\d{4}|\d{2})?$/;
const TIME_ONLY = /^(\d{1,2})(?:[:.]?(\d{2}))?\s*(?:uhr)?$/i;

export function normalizeDate(text) {
  const s = String(text === null || text === undefined ? '' : text).trim();
  if (!s) {
    return '';
  }
  const m = DATE_ONLY.exec(s);
  if (!m) {
    return null;
  }
  const parsed = parseDateTime(m[1] + '.' + m[2] + '.' + (m[3] || ''));
  return parsed ? parsed.text : null;
}

export function normalizeTime(text) {
  const s = String(text === null || text === undefined ? '' : text).trim();
  if (!s) {
    return '';
  }
  const m = TIME_ONLY.exec(s);
  if (!m) {
    return null;
  }
  const hour = Number(m[1]);
  const minute = m[2] === undefined ? 0 : Number(m[2]);
  return hour > 23 || minute > 59 ? null : pad2(hour) + ':' + pad2(minute);
}

// Zustand eines Zeitpunkts ('13.10.2026 05:24') ↔ Datum und Uhrzeit
export function splitDateTime(text) {
  const parts = String(text === null || text === undefined ? '' : text).trim().split(/\s+/);
  return { date: parts[0] || '', time: parts[1] || '' };
}

export function joinDateTime(date, time) {
  return date ? date + (time ? ' ' + time : '') : '';
}

// 'DD.MM.YYYY' ↔ 'YYYY-MM-DD' (Kalender)
export function dateToIso(date) {
  const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(date || '');
  return m ? m[3] + '-' + m[2] + '-' + m[1] : null;
}

export function isoToDate(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  return m ? m[3] + '.' + m[2] + '.' + m[1] : '';
}

// Kurztext eines Zeitpunkts für den Eintrag im Panel: „13.10.“, „13.10., 00:00–05:24“, „12.10. – 13.10.“, „ab 13.10.,
// 05:00“, „bis 13.10., 05:24“; ohne Wert null
export function shortTimeRange(fromText, toText) {
  const from = splitDateTime(fromText);
  const to = splitDateTime(toText);
  const day = (date) => date.slice(0, 6);
  const at = (part) => day(part.date) + (part.time ? ', ' + part.time : '');
  if (from.date && to.date) {
    if (from.date === to.date) {
      return day(from.date) + (from.time || to.time ? ', ' + (from.time || '00:00') + '–' + (to.time || '23:59') : '');
    }
    return at(from) + ' – ' + at(to);
  }
  if (from.date) {
    return 'ab ' + at(from);
  }
  return to.date ? 'bis ' + at(to) : null;
}

// Tage mit Einträgen je Zeitpunkt ('YYYY-MM-DD', Punkte im Kalender): { [time.key]: [...] }
export function buildTimeDays(rows, config) {
  const result = {};
  const data = Array.isArray(rows) ? rows : [];
  timesOf(config).forEach((time) => {
    const days = new Set();
    for (let i = 0; i < data.length; i++) {
      const ms = data[i] ? timeValue(time.value(data[i])) : NaN;
      if (isFinite(ms)) {
        const d = new Date(ms);
        days.add(d.getFullYear() + '-' + pad2(d.getMonth() + 1) + '-' + pad2(d.getDate()));
      }
    }
    result[time.key] = Array.from(days).sort();
  });
  return result;
}

// Zeitstempel einer Zeile in ms (Zahl, Date oder ISO-Text); ohne gültigen Wert NaN
function timeValue(value) {
  if (value === null || value === undefined || value === '') {
    return NaN;
  }
  if (typeof value === 'number') {
    return value;
  }
  return (value instanceof Date ? value : new Date(value)).getTime();
}

// Anzahl gesetzter Filter für die Pille am Knopf „Filter“: je Segment, je Liste mit Auswahl und der Preisbereich
export function activeCount(filters, config) {
  const f = normalize(filters, config);
  let count = 0;
  ['payment', 'storno', 'direction'].concat(segmentsOf(config).map(segment => segment.key)).forEach((key) => {
    if (f[key]) {
      count += 1;
    }
  });
  configOf(config).lists.forEach((list) => {
    if (f[list.key].length) {
      count += 1;
    }
  });
  if (parsePrice(f.priceMin) !== null || parsePrice(f.priceMax) !== null) {
    count += 1;
  }
  timesOf(config).forEach((time) => {
    const fields = timeFields(time);
    if (timeBounds(f[fields.from], f[fields.to])) {
      count += 1;
    }
  });
  return count;
}

// Prüffunktion für grid.filter([fn, '=', true]); null ohne gesetzten Filter. Jeder Aufruf liefert eine neue
// Funktion (grid.filter() übergeht dieselbe Funktion als unverändert).
export function buildPredicate(filters, config) {
  const cfg = configOf(config);
  const f = normalize(filters, config);
  const tests = [];
  if (f.payment) {
    // Bar, Karte (auch externes Terminal) oder Gutschein, wie Spalte und Detail (zahlart.js)
    const gesucht = f.payment;
    tests.push((row) => zahlart(row) === gesucht);
  }
  if (f.storno === 'nur') {
    tests.push((row) => row.is_cancellation == 1);
  } else if (f.storno === 'ohne') {
    tests.push((row) => row.is_cancellation != 1);
  }
  if (f.direction === 'hin') {
    tests.push((row) => row.is_outward_direction == 1);
  } else if (f.direction === 'rueck') {
    tests.push((row) => valueKey(row.is_outward_direction) !== '' && row.is_outward_direction != 1);
  }
  segmentsOf(config).forEach((segment) => {
    const value = f[segment.key];
    if (value !== null && value !== undefined) {
      tests.push((row) => segment.match(value, row));
    }
  });
  cfg.lists.forEach((list) => {
    if (f[list.key].length) {
      const wanted = new Set(f[list.key]);
      tests.push((row) => wanted.has(listKey(list, row)));
    }
  });
  const min = parsePrice(f.priceMin);
  const max = parsePrice(f.priceMax);
  if (min !== null || max !== null) {
    // config.amountValue(row): Betrag aus der Zeile berechnen (Stripe: Cent → Euro), sonst das Feld amountField
    const amountOf = (row) => (cfg.amountValue ? cfg.amountValue(row) : row[cfg.amountField]);
    tests.push((row) => {
      const raw = amountOf(row);
      const price = valueKey(raw) === '' ? NaN : Number(raw);
      return isFinite(price) && (min === null || price >= min) && (max === null || price <= max);
    });
  }
  // Zeitpunkte: Zeilen ohne Wert fallen heraus (z. B. „Rückerstattungszeit bis …“ zeigt nur Rückerstattungen)
  timesOf(config).forEach((time) => {
    const fields = timeFields(time);
    const b = timeBounds(f[fields.from], f[fields.to]);
    if (b) {
      tests.push((row) => {
        const ms = timeValue(time.value(row));
        return isFinite(ms) && (b.min === null || ms >= b.min) && (b.max === null || ms <= b.max);
      });
    }
  });
  if (!tests.length) {
    return null;
  }
  return function saleFilterMatch(row) {
    if (!row) {
      return false;
    }
    for (let i = 0; i < tests.length; i++) {
      if (!tests[i](row)) {
        return false;
      }
    }
    return true;
  };
}

function compareItems(a, b) {
  if ((a.key === '') !== (b.key === '')) {
    return a.key === '' ? 1 : -1;
  }
  return collator.compare(a.text, b.text) || collator.compare(a.key, b.key);
}

// Werte der Listen aus den geladenen Zeilen: [{ key, label, items: [{ key, text, missing }] }]. Eine Liste mit höchstens
// einem Wert entfällt; gewählte Werte, die im Zeitraum fehlen, bleiben zum Abwählen sichtbar (missing).
// labelFor(listKey, key) liefert den Anzeigetext (Fahrer: voller Name statt Login).
export function buildFacets(rows, filters, labelFor, config) {
  const f = normalize(filters, config);
  const label = typeof labelFor === 'function' ? labelFor : defaultLabel;
  const data = Array.isArray(rows) ? rows : [];
  return configOf(config).lists.map((list) => {
    const seen = new Set();
    for (let i = 0; i < data.length; i++) {
      if (data[i]) {
        seen.add(listKey(list, data[i]));
      }
    }
    const selected = f[list.key];
    const keys = Array.from(seen);
    selected.forEach((key) => {
      if (!seen.has(key)) {
        keys.push(key);
      }
    });
    if (keys.length < 2 && !selected.length) {
      return null;
    }
    // icon (optional, z. B. Vertriebskanal der Shop-Tickets): Icon-Name je Wert vor dem Text
    const iconOf = typeof list.icon === 'function' ? list.icon : null;
    const items = keys.map((key) => ({ key, text: label(list.key, key), missing: !seen.has(key), icon: iconOf ? iconOf(key) : null }));
    // sortByKey (Tage als „YYYY-MM-DD“): chronologisch, neueste zuerst, „ohne Angabe“ zuletzt; sonst nach Text
    if (list.sortByKey) {
      items.sort((a, b) => ((a.key === '') !== (b.key === '') ? (a.key === '' ? 1 : -1) : (a.key < b.key ? 1 : a.key > b.key ? -1 : 0)));
    } else {
      items.sort(compareItems);
    }
    return { key: list.key, label: list.label, items };
  }).filter(Boolean);
}

function itemText(items, value) {
  const item = items.find((entry) => entry.value === value);
  return item ? item.desc || item.text : String(value);
}

// gesetzte Filter als Texte, z. B. ['Zahlart: Bar', 'ohne Stornos', 'Fahrer: Anna Berger, Tobias Huber',
// 'Preis: ab 2,00 €']; max kürzt lange Listen („…, Moosburg und 3 weitere“), ohne max vollständig (Druck)
export function describeFilters(filters, labelFor, max, config) {
  const cfg = configOf(config);
  const f = normalize(filters, config);
  const label = typeof labelFor === 'function' ? labelFor : defaultLabel;
  const parts = [];
  if (f.payment) {
    parts.push('Zahlart: ' + itemText(PAYMENT_ITEMS, f.payment));
  }
  if (f.storno) {
    parts.push(itemText(STORNO_ITEMS, f.storno));
  }
  if (f.direction) {
    parts.push('Richtung: ' + itemText(DIRECTION_ITEMS, f.direction));
  }
  segmentsOf(config).forEach((segment) => {
    const value = f[segment.key];
    if (value !== null && value !== undefined) {
      parts.push(segment.label + ': ' + itemText(segment.items, value));
    }
  });
  cfg.lists.forEach((list) => {
    const selected = f[list.key];
    if (!selected.length) {
      return;
    }
    const texts = selected
      .map((key) => ({ key, text: label(list.key, key) }))
      .sort(compareItems)
      .map((item) => item.text);
    const shown = max && texts.length > max ? texts.slice(0, max) : texts;
    const rest = texts.length - shown.length;
    parts.push(list.label + ': ' + shown.join(', ') + (rest ? ' und ' + rest + ' weitere' : ''));
  });
  const min = parsePrice(f.priceMin);
  const top = parsePrice(f.priceMax);
  if (min !== null && top !== null) {
    parts.push(cfg.amountLabel + ': ' + formatEuro(min) + ' bis ' + formatEuro(top));
  } else if (min !== null) {
    parts.push(cfg.amountLabel + ': ab ' + formatEuro(min));
  } else if (top !== null) {
    parts.push(cfg.amountLabel + ': bis ' + formatEuro(top));
  }
  timesOf(config).forEach((time) => {
    const fields = timeFields(time);
    const from = parseDateTime(f[fields.from]);
    const to = parseDateTime(f[fields.to], true);
    if (from && to) {
      parts.push(time.label + ': ' + from.text + ' bis ' + to.text);
    } else if (from) {
      parts.push(time.label + ': ab ' + from.text);
    } else if (to) {
      parts.push(time.label + ': bis ' + to.text);
    }
  });
  return parts;
}

// ---------------------------------------------------------------- Sortierung

// Feld „Sortierung“ (Kundenwunsch 12.10.2026, wörtlich: „sortierung nochmal überarbeiten mit besser gesalteter labels
// evtl dick oder was auch immer und nur was auch sinn macht“): sieben Felder, je mit feldgerechten Richtungstexten und
// einer Standardrichtung beim Wählen. columns = Grid-Spalten (name, sonst dataField), alle in derselben Richtung;
// danach sortiert die Seite immer nach dem Verkaufsdatum (applyToolbarView in TicketSale.vue). Gestrichen sind
// Personen, Zahlart, Tarif und Preisstufe (wenige Werte: das leisten Filter und Gruppierung besser), Verkaufs-ID
// (fortlaufend wie die Verkaufszeit; einzelne Verkäufe findet die Suche) sowie Von und Nach einzeln (zusammen als
// „Strecke“). Richtungstexte klein geschrieben; standalone: Der Knopf zeigt nur die Richtung („Neueste zuerst“),
// sonst Feld und Richtung („Preis · höchster zuerst“).
const ALPHA = { asc: 'A–Z', desc: 'Z–A' };
const STEIGEND = { asc: 'aufsteigend', desc: 'absteigend' };
export const SORT_FIELDS = [
  {
    key: 'zeit',
    label: 'Verkaufszeit',
    columns: ['verkauft_am'],
    dir: 'desc',
    texts: { desc: 'neueste zuerst', asc: 'älteste zuerst' },
    standalone: true
  },
  {
    key: 'preis',
    label: 'Preis',
    columns: ['price'],
    dir: 'desc',
    texts: { desc: 'höchster zuerst', asc: 'niedrigster zuerst' }
  },
  { key: 'ticket', label: 'Ticket', columns: ['ticket_strecke'], dir: 'asc', texts: ALPHA },
  {
    key: 'linie',
    label: 'Linie und Fahrt',
    short: 'Linie',
    columns: ['line_id', 'trip_id'],
    dir: 'asc',
    texts: STEIGEND
  },
  { key: 'fahrer', label: 'Fahrer', columns: ['username'], dir: 'asc', texts: ALPHA },
  { key: 'strecke', label: 'Strecke', columns: ['location_from_name', 'location_to_name'], dir: 'asc', texts: ALPHA },
  { key: 'geraet', label: 'Gerät', columns: ['printer_id'], dir: 'asc', texts: ALPHA }
];
export const DEFAULT_SORT = [['verkauft_am', 'desc']];

// sichtbare Spalten ohne eigenes Feld (Kopfklick): Name und Richtungstexte für Knopf und Menü
const SORT_COLUMNS = {
  payment_method: { label: 'Zahlart', texts: ALPHA },
  ticket_count: { label: 'Personen', texts: STEIGEND }
};

// Spalten mit gleichem Sortierwert: die verborgenen Verkaufsdatum-Spalten wie „Verkauft am“
const SORT_ALIASES = { sale_date: 'verkauft_am', sale_date_grouping: 'verkauft_am' };

// [[spalte, 'asc' | 'desc'], …] vergleichbar machen: Aliase auflösen, wiederholte Spalten entfernen (wirken nicht mehr)
// und das abschließende Verkaufsdatum weglassen, solange davor eine andere Spalte sortiert
export function normalizeSortSpec(entries) {
  const result = [];
  (entries || []).forEach((entry) => {
    if (!entry || (entry[1] !== 'asc' && entry[1] !== 'desc')) {
      return;
    }
    const name = SORT_ALIASES[entry[0]] || entry[0];
    if (!result.some((done) => done[0] === name)) {
      result.push([name, entry[1]]);
    }
  });
  if (result.length > 1 && result[result.length - 1][0] === 'verkauft_am') {
    result.pop();
  }
  return result;
}

export function sameSortSpec(a, b) {
  return (
    Array.isArray(a) &&
    Array.isArray(b) &&
    a.length === b.length &&
    a.every((entry, i) => entry[0] === b[i][0] && entry[1] === b[i][1])
  );
}

export function upperFirst(text) {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

// Sortierung eines Felds; ohne Richtung die Standardrichtung des Felds
export function sortSpecFor(fieldKey, dir) {
  const field = SORT_FIELDS.find((entry) => entry.key === fieldKey);
  if (!field) {
    return DEFAULT_SORT.slice();
  }
  return field.columns.map((column) => [column, dir || field.dir]);
}

// Feld und Richtung einer Sortierung: die Spalten am Anfang der Feldspalten in einer Richtung (ein Kopfklick auf
// „Linie“ sortiert nur line_id und gilt als „Linie und Fahrt“); null, wenn die Sortierung keinem Feld entspricht
export function findSortField(spec) {
  const wanted = normalizeSortSpec(spec);
  if (!wanted.length) {
    return null;
  }
  const dir = wanted[0][1];
  const field = SORT_FIELDS.find(
    (entry) =>
      wanted.length <= entry.columns.length &&
      wanted.every((item, i) => item[0] === entry.columns[i] && item[1] === dir)
  );
  return field ? { field, dir } : null;
}

// Richtungen in Menüfolge, die Standardrichtung zuerst („Neueste zuerst“, „Älteste zuerst“ bzw. „A–Z“, „Z–A“)
function directionsFor(texts, first) {
  const order = first === 'desc' ? ['desc', 'asc'] : ['asc', 'desc'];
  return order.map((dir) => ({ dir, text: upperFirst(texts[dir]) }));
}

// Zustand des Felds „Sortierung“: Feld (key, oder column bei einem Kopfklick ohne eigenes Feld), Richtung, Knopftext
// („Neueste zuerst“, „Preis · höchster zuerst“, „Zahlart · Z–A“), zugänglicher Name ohne Trennpunkt („Preis, höchster
// zuerst“) und die Richtungen für das Menü. Ohne Sortierung (Strg+Klick auf den Kopf) „Unsortiert“.
export function describeSort(spec) {
  const wanted = normalizeSortSpec(spec);
  const found = findSortField(wanted);
  let key = null;
  let column = null;
  let dir = null;
  let label = '';
  let texts = STEIGEND;
  let standalone = false;
  let first = 'asc';
  if (found) {
    key = found.field.key;
    dir = found.dir;
    label = found.field.short || found.field.label;
    texts = found.field.texts;
    standalone = !!found.field.standalone;
    first = found.field.dir;
  } else if (wanted.length) {
    column = wanted[0][0];
    dir = wanted[0][1];
    const known = SORT_COLUMNS[column];
    label = known ? known.label : column;
    texts = known ? known.texts : STEIGEND;
  } else {
    return { key: null, column: null, dir: null, text: 'Unsortiert', name: 'unsortiert', directions: [] };
  }
  const dirText = texts[dir];
  return {
    key,
    column,
    dir,
    text: standalone ? upperFirst(dirText) : label + ' · ' + dirText,
    name: standalone ? upperFirst(dirText) : label + ', ' + dirText,
    directions: directionsFor(texts, first)
  };
}

// Richtungstext eines Felds im Menü („Höchster zuerst“, „A–Z“)
export function sortDirectionText(field, dir) {
  return upperFirst(field.texts[dir || field.dir]);
}

// Richtung des abschließenden Verkaufsdatums: wie „Verkauft am“, wenn danach sortiert wird, sonst neueste zuerst
export function sortDateOrder(spec) {
  const first = spec && spec[0];
  return first && first[0] === 'verkauft_am' ? first[1] : 'desc';
}

// Sortierwerte: Text ohne Groß-/Kleinschreibung und Akzente („Özdemir“ bei O, ß = ss)
export function foldText(value) {
  return valueKey(value)
    .toLowerCase()
    .replace(/ß/g, 'ss')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');
}

// Zahlen in Texten in natürlicher Folge („12“ vor „100“, „N2“ vor „N10“): Ziffernfolgen auf 12 Stellen auffüllen
export function naturalKey(value) {
  return foldText(value).replace(/\d+/g, (digits) => digits.replace(/^0+(?=\d)/, '').padStart(12, '0'));
}
